import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Globe, Send, Phone } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../contexts/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-[#0a0a0a] border border-white/10 px-4 py-3 font-body text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#39FF14] focus:shadow-[0_0_18px_rgba(57,255,20,0.2)] transition-all";
  const labelCls =
    "font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 block";

  return (
    <div data-testid="contact-page">
      <section className="relative py-24 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="label-tech">{t.contact.title}</span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-bold uppercase tracking-tight">
            <span className="chrome-text">{t.contact.title}</span>
          </h1>
          <p className="mt-5 text-zinc-400 font-body max-w-2xl">{t.contact.subtitle}</p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-[1fr_0.8fr] gap-12">
        <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t.contact.name}</label>
              <input
                required
                name="name"
                value={form.name}
                onChange={onChange}
                className={inputCls}
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className={labelCls}>{t.contact.email}</label>
              <input
                required
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                className={inputCls}
                data-testid="contact-email-input"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t.contact.phone}</label>
              <input
                name="phone"
                value={form.phone}
                onChange={onChange}
                className={inputCls}
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <label className={labelCls}>{t.contact.subject}</label>
              <input
                name="subject"
                value={form.subject}
                onChange={onChange}
                className={inputCls}
                data-testid="contact-subject-input"
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>{t.contact.message}</label>
            <textarea
              required
              name="message"
              value={form.message}
              onChange={onChange}
              rows={7}
              className={inputCls}
              data-testid="contact-message-input"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="btn-neon disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="contact-submit-button"
          >
            <Send size={16} />
            {submitting ? t.common.loading : t.contact.send}
          </button>
        </form>

        <aside className="bg-[#080808] border border-white/5 p-8 h-fit">
          <div className="label-tech mb-6">{t.contact.title}</div>
          <ul className="space-y-6">
            <li>
              <div className={labelCls}>{t.contact.address_label}</div>
              <div className="flex items-start gap-3 text-zinc-200 font-body">
                <MapPin size={18} className="text-[#39FF14] mt-0.5 shrink-0" />
                <span>{t.contact.address_value}</span>
              </div>
            </li>
            <li>
              <div className={labelCls}>{t.contact.phone_label}</div>
              <div className="flex items-start gap-3 text-zinc-200 font-body">
                <Phone size={18} className="text-[#39FF14] mt-0.5 shrink-0" />
                <a
                  href="tel:+33610443864"
                  className="hover:text-[#39FF14] transition-colors font-medium"
                  data-testid="contact-phone-link"
                >
                  {t.contact.phone_value}
                </a>
              </div>
            </li>
            <li>
              <div className={labelCls}>{t.contact.email_label}</div>
              <div className="flex items-start gap-3 text-zinc-200 font-body">
                <Mail size={18} className="text-[#39FF14] mt-0.5 shrink-0" />
                <a
                  href={`mailto:${t.contact.email_value}`}
                  className="hover:text-[#39FF14] transition-colors"
                >
                  {t.contact.email_value}
                </a>
              </div>
            </li>
            <li>
              <div className={labelCls}>{t.contact.web_label}</div>
              <div className="flex items-start gap-3 text-zinc-200 font-body">
                <Globe size={18} className="text-[#39FF14] mt-0.5 shrink-0" />
                <span>{t.contact.web_value}</span>
              </div>
            </li>
          </ul>

          <div className="mt-8 p-5 border border-[#39FF14]/30 bg-[#39FF14]/5">
            <div className="font-mono text-xs uppercase tracking-widest text-[#39FF14] mb-2">
              France
            </div>
            <p className="text-sm text-zinc-300 font-body">
              Société française — expédition dans toute la France depuis Saint-Jean-de-Gonville (Ain).
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
